import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Shield, LayoutDashboard, Plus, Edit3, Copy, Power, Trash2, RefreshCw,
  AlertCircle, Check, X, Search,
} from 'lucide-react';
import { DASHBOARD_DISPLAY_NAMES } from '../../config/hospitalRoles';
import { buildApiUrl } from '../../utils/apiSetup';

const API = buildApiUrl('/api/v1/roles');
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  'Content-Type': 'application/json',
});

const DASHBOARD_OPTIONS = [
  { value: 'admin', label: DASHBOARD_DISPLAY_NAMES.admin || 'Admin Dashboard' },
  { value: 'checker', label: DASHBOARD_DISPLAY_NAMES.checker || 'Checker Dashboard' },
  { value: 'maker', label: DASHBOARD_DISPLAY_NAMES.maker || 'Maker Dashboard' },
  { value: 'user', label: DASHBOARD_DISPLAY_NAMES.employee || 'Employee Dashboard' },
];

const VISIBILITY_OPTIONS = [
  { value: 'self', label: 'Self' },
  { value: 'team', label: 'Team' },
  { value: 'department', label: 'Department' },
  { value: 'branch', label: 'Branch' },
  { value: 'organization', label: 'Organization' },
];

const emptyForm = () => ({
  name: '',
  description: '',
  dashboard_tier: 'user',
  hierarchy_level: 6,
  visibility_scope: 'self',
  display_order: 100,
  permissions: [],
});

const RolePermissionMatrix = () => {
  const [roles, setRoles] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rolesRes, permsRes] = await Promise.all([
        fetch(`${API}/?hospital_only=true&include_permissions=true`, { headers: authHeaders() }),
        fetch(`${API}/permissions`, { headers: authHeaders() }),
      ]);
      const rolesData = await rolesRes.json();
      const permsData = await permsRes.json();
      if (!rolesRes.ok || !rolesData.success) {
        throw new Error(rolesData.detail || 'Failed to load roles');
      }
      setRoles(rolesData.roles || []);
      setGroupedPermissions(permsData.grouped_permissions || {});
    } catch (e) {
      setError(e.message || 'Failed to load role data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.display_name?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    );
  }, [roles, search]);

  const openCreate = () => {
    setForm(emptyForm());
    setSelectedId(null);
    setModal('create');
  };

  const openEdit = (role) => {
    setSelectedId(role.id);
    setForm({
      name: role.display_name || role.description || role.name,
      description: role.description || role.display_name || '',
      dashboard_tier: role.dashboard_tier || 'user',
      hierarchy_level: role.hierarchy_level ?? 6,
      visibility_scope: role.visibility_scope || 'self',
      display_order: role.display_order ?? 100,
      permissions: [...(role.permissions || [])],
    });
    setModal('edit');
  };

  const togglePermission = (perm) => {
    setForm((prev) => {
      const has = prev.permissions.includes(perm);
      return {
        ...prev,
        permissions: has
          ? prev.permissions.filter((p) => p !== perm)
          : [...prev.permissions, perm],
      };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        description: form.description || form.name,
        permissions: form.permissions,
        dashboard_tier: form.dashboard_tier,
        hierarchy_level: Number(form.hierarchy_level),
        visibility_scope: form.visibility_scope,
        display_order: Number(form.display_order),
      };
      const url = modal === 'edit' ? `${API}/${selectedId}` : API;
      const method = modal === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(buildApiUrl(url), { method, headers: authHeaders(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.error || 'Save failed');
      }
      setSuccess(modal === 'edit' ? 'Role updated' : 'Role created');
      setModal(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async (role) => {
    setError('');
    try {
      const res = await fetch(`${API}/${role.id}/clone`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ new_name: `${role.display_name || role.name} Copy` }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Clone failed');
      setSuccess('Role cloned');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleToggleStatus = async (role) => {
    const next = role.status === 'active' ? 'inactive' : 'active';
    setError('');
    try {
      const res = await fetch(`${API}/${role.id}/status`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Status update failed');
      setSuccess(next === 'active' ? 'Role activated' : 'Role deactivated');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (role) => {
    const assigned = (role.assignment_counts?.total ?? role.user_count ?? 0) > 0;
    if (assigned) {
      setError('Role is in use. Deactivate it instead of deleting.');
      return;
    }
    if (!window.confirm(`Permanently delete role "${role.display_name || role.name}"?`)) return;
    setError('');
    try {
      const res = await fetch(`${API}/${role.id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Delete failed');
      setSuccess('Role deleted');
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {(error || success) && (
        <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {error ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          <span className="flex-1">{error || success}</span>
          <button type="button" onClick={() => { setError(''); setSuccess(''); }}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
        <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Role & Permission Management</h3>
              <p className="text-xs text-gray-500">Database-driven roles — new roles appear in all employee forms automatically</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="search"
                placeholder="Search roles…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-3 py-2 border rounded-lg text-sm w-48"
              />
            </div>
            <button type="button" onClick={load} className="p-2 border rounded-lg hover:bg-gray-50" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4" /> Create Role
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left">Role</th>
                <th className="px-4 py-2 text-left">Dashboard</th>
                <th className="px-4 py-2 text-left">Hierarchy</th>
                <th className="px-4 py-2 text-left">Visibility</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Assigned</th>
                <th className="px-4 py-2 text-left">Permissions</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.display_name || r.description}</p>
                    <p className="text-xs text-gray-500">{r.name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs capitalize">
                      <LayoutDashboard className="w-3 h-3" />
                      {DASHBOARD_OPTIONS.find((d) => d.value === r.dashboard_tier)?.label || r.dashboard_tier}
                    </span>
                  </td>
                  <td className="px-4 py-3">L{r.hierarchy_level}</td>
                  <td className="px-4 py-3 capitalize">{r.visibility_scope}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${r.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                      {r.status || 'active'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {(r.assignment_counts?.employees ?? 0)} emp · {(r.assignment_counts?.users ?? r.user_count ?? 0)} users
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(r.permissions || []).slice(0, 4).map((p) => (
                        <span key={p} className="text-xs bg-gray-100 px-1 rounded">{p}</span>
                      ))}
                      {(r.permissions || []).length > 4 && (
                        <span className="text-xs text-gray-500">+{r.permissions.length - 4}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" onClick={() => openEdit(r)} className="p-1.5 hover:bg-gray-100 rounded" title="Edit">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => handleClone(r)} className="p-1.5 hover:bg-gray-100 rounded" title="Clone">
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToggleStatus(r)}
                        className="p-1.5 hover:bg-gray-100 rounded"
                        title={r.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        <Power className={`w-4 h-4 ${r.status === 'active' ? 'text-green-600' : 'text-gray-400'}`} />
                      </button>
                      {!r.is_system_role && (
                        <button type="button" onClick={() => handleDelete(r)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No roles found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h4 className="font-semibold">{modal === 'edit' ? 'Edit Role' : 'Create Role'}</h4>
              <button type="button" onClick={() => setModal(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Display name</span>
                  <input
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value, description: e.target.value })}
                    placeholder="e.g. Senior Nurse"
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Dashboard</span>
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.dashboard_tier}
                    onChange={(e) => setForm({ ...form, dashboard_tier: e.target.value })}
                  >
                    {DASHBOARD_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Hierarchy level (1–6)</span>
                  <input
                    type="number"
                    min={1}
                    max={6}
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.hierarchy_level}
                    onChange={(e) => setForm({ ...form, hierarchy_level: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Visibility scope</span>
                  <select
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.visibility_scope}
                    onChange={(e) => setForm({ ...form, visibility_scope: e.target.value })}
                  >
                    {VISIBILITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="font-medium text-gray-700">Display order</span>
                  <input
                    type="number"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.display_order}
                    onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  />
                </label>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Permissions</p>
                <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-3">
                  {Object.entries(groupedPermissions).map(([group, perms]) => (
                    <div key={group}>
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">{group}</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(perms).map((perm) => (
                          <label key={perm} className="inline-flex items-center gap-1 text-xs bg-gray-50 px-2 py-1 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(perm)}
                              onChange={() => togglePermission(perm)}
                            />
                            {perm}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 border rounded-lg">Cancel</button>
              <button
                type="button"
                disabled={saving || !form.name.trim()}
                onClick={handleSave}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolePermissionMatrix;
